import { Controller } from "@hotwired/stimulus"
import { formatValue } from "../lib/number_format"
import { track } from "../lib/umami"
import { freebetValue, oddsWarning, parseNumeric } from "../lib/odds_math"

// Calculateur de valeur d'un freebet (/calculateur-freebet, #2726).
//
// Tout le calcul est client, dans odds_math.freebetValue. Les cotes des autres
// issues, facultatives, se saisissent dans un seul champ séparées par des espaces
// ou des points-virgules (« 3,40 ; 3,60 ») ; là où la décimale est un point
// (anglais), la virgule sépare aussi les cotes (« 3.40, 3.60 »). Renseignées, elles
// remplacent l'overround saisi par celui du marché.
//
// Seules les cotes décimales sont acceptées : un signe en tête (« +200 », cote
// américaine) serait sinon lu comme une cote décimale de 200.
export default class extends Controller {
  static targets = [ "amount", "odds", "overround", "otherOdds", "results", "warning", "warningText", "copyButton" ]
  static values = {
    locale: { type: String, default: "fr" },
    messages: { type: Object, default: {} }
  }

  connect() {
    this.defaults = this.fields().map((field) => field.value)
    this.copyLabel = this.hasCopyButtonTarget ? this.copyButtonTarget.textContent : null
    this.compute()
  }

  compute(event) {
    this.trackUse(event)

    const otherOdds = this.otherOdds()
    const warning = this.firstWarning(otherOdds)
    const result = warning ? null : freebetValue({
      amount: this.amountTarget.value,
      odds: this.oddsTarget.value,
      overroundPoints: this.overroundTarget.value,
      otherOdds
    })

    this.renderWarning(warning)
    this.renderResults(result)
  }

  applyPreset(event) {
    track("freebetCalculatorPreset_applied", { odds: event.currentTarget.dataset.odds })
    this.oddsTarget.value = event.currentTarget.dataset.odds
    this.compute()
  }

  reset() {
    track("freebetCalculator_reset")
    this.fields().forEach((field, index) => { field.value = this.defaults[index] })
    this.compute()
  }

  async copy() {
    if (!this.hasCopyButtonTarget) return

    try {
      await navigator.clipboard.writeText(this.summary())
      track("freebetCalculatorResult_copied")
      this.copyButtonTarget.textContent = this.messagesValue.copied || this.copyLabel
      window.setTimeout(() => { this.copyButtonTarget.textContent = this.copyLabel }, 2000)
    } catch {
      // Presse-papiers indisponible : pas de confirmation trompeuse.
    }
  }

  preventSubmit(event) {
    event.preventDefault()
    this.compute()
  }

  // -------------------------------------------------------------------------

  // Une seule fois par visite, au premier changement saisi : le compute() du
  // connect() ne compte pas comme un usage.
  trackUse(event) {
    if (this.used || event?.type !== "input") return

    this.used = true
    track("freebetCalculator_used")
  }

  fields() {
    return [ this.amountTarget, this.oddsTarget, this.overroundTarget, this.otherOddsTarget ]
  }

  otherOdds() {
    const separator = this.localeValue.startsWith("en") ? /[\s;,]+/ : /[\s;]+/
    return this.otherOddsTarget.value.split(separator).filter((value) => value !== "")
  }

  firstWarning(otherOdds) {
    const amount = parseNumeric(this.amountTarget.value)
    if (amount === null || amount <= 0) return "invalid_amount"

    for (const value of [ this.oddsTarget.value, ...otherOdds ]) {
      if (/^\s*[+-]/.test(value)) return "decimal_odds_only"
      const code = oddsWarning(value)
      if (code) return code
    }

    if (otherOdds.length > 0) return null

    const overround = parseNumeric(this.overroundTarget.value)
    if (overround === null) return "invalid_number"
    if (overround < 0) return "negative_overround"
    return null
  }

  renderResults(result) {
    const values = result === null ? {} : {
      win_return: result.winReturn,
      implied_probability: result.impliedProbability * 100,
      fair_probability: result.fairProbability * 100,
      applied_overround: result.overround * 100,
      expected_value: result.expectedValue,
      value_share: result.valueShare * 100
    }

    this.resultsTarget.querySelectorAll("[data-output]").forEach((output) => {
      const value = values[output.dataset.output]
      output.textContent = value === null || value === undefined
        ? "—"
        : formatValue(value, output.dataset.format, this.localeValue)
    })
  }

  renderWarning(code) {
    this.warningTarget.hidden = !code
    this.warningTextTarget.textContent = code ? (this.messagesValue[code] || "") : ""
  }

  summary() {
    const inputs = this.fields()
      .filter((field) => field.value.trim() !== "")
      .map((field) => `${field.labels[0]?.textContent.trim()}: ${field.value}`)
    const results = Array.from(this.resultsTarget.querySelectorAll(".calc-result")).map((result) =>
      `${result.querySelector("dt").textContent}: ${result.querySelector("dd").textContent}`)

    return [ ...inputs, ...results ].join("\n")
  }
}
